/**
 * One operator's month at a glance: which shift they are on each day, and any
 * leave over the top.
 *
 * Shifts and leaves live on separate screens, but they only make sense
 * together — a shift assignment means nothing on a day the person is off. The
 * server resolves both from the same rules the rest of the system uses, so
 * this view cannot drift from what the clock-in check actually applies.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

// Leave colouring by state. Pending is deliberately distinct from approved:
// a planner must not read a request as a confirmed absence.
const LEAVE_STYLE = {
  approved: 'bg-red-100 text-red-800 border-red-300',
  pending: 'bg-amber-100 text-amber-800 border-amber-300',
  pending_admin: 'bg-amber-100 text-amber-800 border-amber-300',
  rejected: 'bg-gray-100 text-gray-400 border-gray-200 line-through',
};

export default function ShiftCalendar({ users = [] }) {
  const { token } = useAuth();
  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const today = new Date();
  const [userId, setUserId] = useState('');
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);   // 1-12
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!userId) { setData(null); return; }
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/shifts/calendar', {
        ...authHeader,
        params: { user_id: userId, year, month },
      });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not load the calendar');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [authHeader, userId, year, month]);

  useEffect(() => { load(); }, [load]);

  const step = (delta) => {
    const m = month + delta;
    if (m < 1) { setMonth(12); setYear((y) => y - 1); }
    else if (m > 12) { setMonth(1); setYear((y) => y + 1); }
    else setMonth(m);
  };

  // Pad the grid so the 1st lands under its weekday. getDay() is Sunday-based;
  // this calendar starts on Monday, hence the shift.
  const leadingBlanks = useMemo(() => {
    const dow = new Date(year, month - 1, 1).getDay();
    return (dow + 6) % 7;
  }, [year, month]);

  const todayIso = new Date().toISOString().slice(0, 10);

  const summary = useMemo(() => {
    const days = data?.days || [];
    return {
      working: days.filter((d) => d.shift && !d.leaves.some((l) => l.status === 'approved')).length,
      leave: days.filter((d) => d.leaves.some((l) => l.status === 'approved')).length,
      unassigned: days.filter((d) => !d.shift).length,
    };
  }, [data]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-blue-600" />
          Shift Calendar
        </h2>

        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Choose an operator…</option>
            {users.map((u) => (
              <option key={u.id || u._id} value={u.id || u._id}>
                {u.name}{u.role ? ` — ${u.role.replace(/_/g, ' ')}` : ''}
              </option>
            ))}
          </select>

          <div className="flex items-center gap-1">
            <button
              onClick={() => step(-1)}
              className="p-2 rounded-md border border-gray-300 hover:bg-gray-50"
              title="Previous month"
            ><ChevronLeft className="w-4 h-4" /></button>
            <span className="px-3 text-sm font-medium text-gray-800 min-w-[9rem] text-center">
              {MONTHS[month - 1]} {year}
            </span>
            <button
              onClick={() => step(1)}
              className="p-2 rounded-md border border-gray-300 hover:bg-gray-50"
              title="Next month"
            ><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 px-4 py-2 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
          {error}
        </div>
      )}

      {!userId ? (
        <p className="py-12 text-center text-sm text-gray-400">
          Choose an operator to see their shifts and leave for the month.
        </p>
      ) : loading ? (
        <p className="py-12 text-center text-sm text-gray-400 animate-pulse">Loading…</p>
      ) : !data ? null : (
        <>
          <div className="flex flex-wrap gap-4 mb-3 text-sm">
            <span className="text-gray-600">
              <strong className="text-gray-800">{summary.working}</strong> working
            </span>
            <span className="text-gray-600">
              <strong className="text-red-700">{summary.leave}</strong> on leave
            </span>
            <span className="text-gray-600">
              <strong className="text-gray-500">{summary.unassigned}</strong> no shift assigned
            </span>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-1">
            {WEEKDAYS.map((w) => (
              <div key={w} className="text-xs font-semibold text-gray-500 text-center py-1">{w}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: leadingBlanks }).map((_, i) => (
              <div key={`blank-${i}`} className="min-h-[92px] rounded-md bg-gray-50/50" />
            ))}

            {data.days.map((d) => {
              const approved = d.leaves.find((l) => l.status === 'approved');
              const other = d.leaves.find((l) => l.status !== 'approved' && l.status !== 'rejected');
              const isToday = d.date === todayIso;
              const dayNum = Number(d.date.slice(8, 10));

              return (
                <div
                  key={d.date}
                  className={`min-h-[92px] rounded-md border p-1.5 flex flex-col gap-1 ${
                    isToday ? 'border-blue-500 ring-1 ring-blue-200' : 'border-gray-200'
                  } ${approved ? 'bg-red-50/40' : 'bg-white'}`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-semibold ${isToday ? 'text-blue-700' : 'text-gray-700'}`}>
                      {dayNum}
                    </span>
                    {isToday && <span className="text-[9px] text-blue-600 font-medium">TODAY</span>}
                  </div>

                  {/* Shift chip, tinted with the template's own colour so the
                      pattern of a rotation is visible down the month. */}
                  {d.shift ? (
                    <div
                      className="text-[10px] leading-tight rounded px-1 py-0.5 border"
                      style={{
                        backgroundColor: `${d.shift.color || '#e5e7eb'}55`,
                        borderColor: d.shift.color || '#d1d5db',
                      }}
                      title={`${d.shift.name} ${d.shift.start}–${d.shift.end}`}
                    >
                      <div className="font-semibold text-gray-800 truncate">{d.shift.name}</div>
                      <div className="text-gray-600">{d.shift.start}–{d.shift.end}</div>
                    </div>
                  ) : (
                    <div className="text-[10px] text-gray-300 italic">No shift</div>
                  )}

                  {/* Leave sits on top of the shift rather than replacing it:
                      the roster still says they were rostered, which is what
                      makes the clash visible. */}
                  {(approved || other) && (() => {
                    const lv = approved || other;
                    return (
                      <div
                        className={`text-[10px] leading-tight rounded px-1 py-0.5 border ${
                          LEAVE_STYLE[lv.status] || LEAVE_STYLE.pending
                        }`}
                        title={`${lv.leave_type || 'Leave'}${lv.reason ? ` — ${lv.reason}` : ''}`}
                      >
                        <div className="font-semibold truncate">
                          {lv.half_day ? 'Half day' : 'Leave'}
                          {lv.status !== 'approved' ? ' (req)' : ''}
                        </div>
                        {lv.leave_type && <div className="truncate">{lv.leave_type}</div>}
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-4 mt-4 text-xs text-gray-500">
            {(data.templates || []).map((t) => (
              <span key={t.id} className="inline-flex items-center gap-1.5">
                <span
                  className="w-3 h-3 rounded-sm border"
                  style={{ backgroundColor: `${t.color || '#e5e7eb'}55`, borderColor: t.color || '#d1d5db' }}
                />
                {t.name} {t.start}–{t.end}
              </span>
            ))}
            <span className="inline-flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm bg-red-100 border border-red-300" /> Approved leave
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm bg-amber-100 border border-amber-300" /> Leave requested
            </span>
          </div>
        </>
      )}
    </div>
  );
}
